import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import type { VerificationRequest } from "../ValidacionIdentidadPageNew";

interface Props {
    isOpen: boolean;
    selectedReq: VerificationRequest | null;
    loadingDetail?: boolean;
    isUpdating?: boolean;
    onClose: () => void;
    onApprove: () => void;
    onReject: () => void;
    onShowToast: (msg: string, type: "success" | "info" | "warning" | "danger") => void;
}

const ValidacionDrawer: React.FC<Props> = ({ isOpen, selectedReq, loadingDetail, isUpdating, onClose, onApprove, onReject, onShowToast }) => {
    // Estado para las notas internas
    const [notes, setNotes] = useState("");

    if (!selectedReq) return null;

    const riskColor = selectedReq.riskScore < 30 ? '#10b981' : selectedReq.riskScore < 60 ? '#f59e0b' : '#ef4444';
    
    // Contamos los documentos dinámicamente
    const approvedDocsCount = selectedReq.docs.filter(d => d.status === 'ok').length;
    const totalDocs = selectedReq.docs.length;
    const selfieDoc = selectedReq.docs.find(d => d.name.toLowerCase().includes('selfie'));

    // Verificaciones basadas en el estado real de la API
    const checks = [
        { 
            icon: approvedDocsCount > 0 ? 'ok' : 'warn', 
            ok: approvedDocsCount > 0, 
            text: 'Coincidencia de nombre con docs', 
            sub: approvedDocsCount > 0 ? 'Documentos presentes para cruce de datos' : 'Esperando documentos' 
        },
        { 
            icon: selfieDoc?.status === 'ok' ? 'ok' : selfieDoc?.status === 'missing' ? 'fail' : 'warn', 
            ok: selfieDoc?.status === 'ok', 
            text: 'Verificación de Rostro (Selfie)', 
            sub: selfieDoc?.status === 'ok' ? 'Rostro verificado' : 'Selfie faltante o en revisión' 
        },
        { 
            icon: 'ok', 
            ok: true, 
            text: 'Lista negra global', 
            sub: 'Sin coincidencias en bases de fraude' 
        },
        { 
            icon: approvedDocsCount === totalDocs ? 'ok' : 'warn', 
            ok: approvedDocsCount === totalDocs, 
            text: 'Documentación completa', 
            sub: `${approvedDocsCount}/${totalDocs} documentos aprobados` 
        },
    ];

    return (
        <>
            <div 
                className={`fixed inset-0 bg-gray-900/30 backdrop-blur-[2px] z-40 transition-opacity duration-300 ${isOpen ? "opacity-100 visible" : "opacity-0 invisible"}`} 
                onClick={onClose}
            ></div>

            <div className={`fixed inset-y-0 right-0 z-50 w-full max-w-[400px] bg-white border-l border-gray-200 flex flex-col overflow-y-auto transform transition-transform duration-300 shadow-2xl ${isOpen ? "translate-x-0" : "translate-x-full"}`}>
                
                {(isUpdating || loadingDetail) && (
                    <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-[60] flex flex-col items-center justify-center text-gray-700">
                        <Loader2 className="h-10 w-10 animate-spin text-[#4f6df5] mb-2" />
                        <p className="text-sm font-semibold">{isUpdating ? "Procesando..." : "Cargando documentos..."}</p>
                    </div>
                )}

                <div className="p-[18px] px-[20px] border-b border-gray-100 sticky top-0 bg-white/90 backdrop-blur-md z-10 flex justify-between items-center">
                    <h3 className="text-[14px] font-bold text-gray-900">Revisión de identidad</h3>
                    <button className="w-[30px] h-[30px] rounded-lg bg-gray-50 border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-all flex items-center justify-center" onClick={onClose} disabled={isUpdating}>✕</button>
                </div>

                <div className="m-[20px] bg-white border border-gray-200 rounded-[12px] p-[18px] flex items-center gap-[14px] shadow-sm">
                    <div className={`w-[52px] h-[52px] rounded-full flex-shrink-0 flex items-center justify-center text-[19px] font-bold text-white bg-gradient-to-br ${selectedReq.avatarGradient}`}>
                        {selectedReq.initials}
                    </div>
                    <div>
                        <div className="text-[15px] font-bold text-gray-900 mb-[3px]">{selectedReq.name}</div>
                        <div className="text-[12px] text-gray-500 capitalize">{selectedReq.role} · {selectedReq.date}</div>
                        <div className="flex gap-[6px] mt-[8px] flex-wrap">
                            <span className={`text-[9px] font-bold px-[7px] py-[2px] rounded-[5px] uppercase tracking-[0.5px] ${selectedReq.status === 'approved' ? 'bg-emerald-50 text-emerald-600' : selectedReq.status === 'rejected' ? 'bg-red-50 text-red-600' : selectedReq.status === 'review' ? 'bg-indigo-50 text-[#4f6df5]' : 'bg-amber-50 text-amber-600'}`}>
                                {selectedReq.status === 'approved' ? 'Aprobado' : selectedReq.status === 'rejected' ? 'Rechazado' : selectedReq.status === 'review' ? 'En revisión' : 'Pendiente'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="px-[20px] py-[14px] border-b border-gray-100">
                    <div className="text-[10px] font-bold uppercase tracking-[0.8px] text-gray-400 mb-[12px]">Documentos ({approvedDocsCount}/{totalDocs})</div>
                    <div className="grid grid-cols-2 gap-[10px]">
                        {selectedReq.docs.map((doc, i) => (
                            <div key={i} className={`bg-white border rounded-lg overflow-hidden transition-colors ${doc.status === 'ok' ? 'border-emerald-200' : doc.status === 'missing' ? 'border-red-200' : 'border-gray-200'}`}>
                                <div className="h-[80px] bg-gray-50 border-b border-gray-100 flex items-center justify-center text-[28px] relative overflow-hidden group">
                                    {doc.docLink ? (
                                        <>
                                            <img src={doc.docLink} alt={doc.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                            <a href={doc.docLink} target="_blank" rel="noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-white text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm">
                                                Ver original
                                            </a>
                                        </>
                                    ) : (
                                        doc.emoji
                                    )}
                                    <span className={`absolute bottom-1 right-1 text-[9px] font-bold px-[6px] py-[2px] rounded ${doc.status === 'ok' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : doc.status === 'missing' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-amber-50 text-amber-600 border border-amber-100'}`}>
                                        {doc.status === 'ok' ? '✓ OK' : doc.status === 'missing' ? '✗ Falta' : '⏳ Pend.'}
                                    </span>
                                </div>
                                <div className="p-[8px] px-[10px]">
                                    <div className="text-[11px] font-semibold text-gray-800 mb-[6px] truncate" title={doc.name}>{doc.name}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="px-[20px] py-[14px] border-b border-gray-100">
                    <div className="text-[10px] font-bold uppercase tracking-[0.8px] text-gray-400 mb-[12px]">Verificaciones automáticas</div>
                    <div className="flex flex-col gap-0">
                        {checks.map((c, i) => (
                            <div key={i} className={`flex items-center gap-[10px] py-[9px] ${i === checks.length - 1 ? '' : 'border-b border-gray-100'}`}>
                                <div className={`w-[26px] h-[26px] rounded-full flex-shrink-0 flex items-center justify-center ${c.icon === 'ok' ? 'bg-emerald-50' : c.icon === 'warn' ? 'bg-amber-50' : 'bg-red-50'}`}>
                                    <svg width="10" height="10" fill="none" stroke={c.ok ? '#10b981' : c.icon === 'fail' ? '#ef4444' : '#f59e0b'} strokeWidth="2.5" viewBox="0 0 24 24">
                                        {c.ok ? <polyline points="20 6 9 17 4 12"/> : <><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>}
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <p className="text-[12px] text-gray-900 m-0">{c.text}</p>
                                    <span className="text-[10px] text-gray-500 m-0">{c.sub}</span>
                                </div>
                                <span className={`text-[11px] font-bold ${c.ok ? 'text-emerald-600' : c.icon === 'fail' ? 'text-red-600' : 'text-amber-500'}`}>
                                    {c.ok ? 'OK' : 'Revisar'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="px-[20px] py-[14px] border-b border-gray-100">
                    <div className="text-[10px] font-bold uppercase tracking-[0.8px] text-gray-400 mb-[12px]">Nivel de riesgo</div>
                    <div className="flex justify-between text-[12px] mb-[4px]">
                        <span className="text-gray-600">Puntuación estimada</span>
                        <span style={{ fontWeight: 700, color: riskColor }}>
                            {selectedReq.riskScore < 30 ? 'Bajo' : selectedReq.riskScore < 60 ? 'Medio' : 'Alto'} ({selectedReq.riskScore}/100)
                        </span>
                    </div>
                    <div className="h-[7px] bg-gray-100 rounded-[4px] overflow-hidden mt-[8px]">
                        <div className="h-full rounded-[4px]" style={{ width: `${selectedReq.riskScore}%`, background: `linear-gradient(90deg, #10b981, ${riskColor})` }}></div>
                    </div>
                </div>

                <div className="px-[20px] py-[14px] border-b border-gray-100">
                    <div className="text-[10px] font-bold uppercase tracking-[0.8px] text-gray-400 mb-[12px]">Historial de validación</div>
                    <div className="flex flex-col">
                        <div className="flex gap-[10px] py-[8px] relative">
                            {approvedDocsCount > 0 && <div className="absolute left-[10px] top-[28px] bottom-0 w-[1px] bg-gray-200"></div>}
                            <div className="w-[20px] h-[20px] rounded-full flex-shrink-0 mt-[1px] flex items-center justify-center bg-indigo-50">
                                <svg width="10" height="10" fill="none" stroke="#4f6df5" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/></svg>
                            </div>
                            <div>
                                <p className="text-[12px] text-gray-900 m-0">Solicitud iniciada por el usuario</p>
                                <span className="text-[10px] text-gray-500">{selectedReq.date}</span>
                            </div>
                        </div>
                        {approvedDocsCount > 0 && (
                            <div className="flex gap-[10px] py-[8px] relative">
                                {selectedReq.status === 'approved' && <div className="absolute left-[10px] top-[28px] bottom-0 w-[1px] bg-gray-200"></div>}
                                <div className="w-[20px] h-[20px] rounded-full flex-shrink-0 mt-[1px] flex items-center justify-center bg-emerald-50">
                                    <svg width="10" height="10" fill="none" stroke="#10b981" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                                </div>
                                <div>
                                    <p className="text-[12px] text-gray-900 m-0">Documentos procesados ({approvedDocsCount})</p>
                                </div>
                            </div>
                        )}
                        {selectedReq.status === 'approved' && (
                            <div className="flex gap-[10px] py-[8px] relative">
                                <div className="w-[20px] h-[20px] rounded-full flex-shrink-0 mt-[1px] flex items-center justify-center bg-emerald-100">
                                    <svg width="10" height="10" fill="none" stroke="#059669" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                                </div>
                                <div>
                                    <p className="text-[12px] text-emerald-700 font-bold m-0">Identidad Aprobada</p>
                                </div>
                            </div>
                        )}
                        {selectedReq.status === 'rejected' && (
                            <div className="flex gap-[10px] py-[8px] relative">
                                <div className="w-[20px] h-[20px] rounded-full flex-shrink-0 mt-[1px] flex items-center justify-center bg-red-100">
                                    <svg width="10" height="10" fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                </div>
                                <div>
                                    <p className="text-[12px] text-red-700 font-bold m-0">Identidad Rechazada</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* -------------------------------------------------------------
                    SECCIÓN DE NOTAS INTERNAS (ACTUALIZADA) 
                ------------------------------------------------------------- */}
                <div className="px-[20px] py-[14px] border-b border-gray-100">
                    <div className="flex justify-between items-center mb-[12px]">
                        <div className="text-[10px] font-bold uppercase tracking-[0.8px] text-gray-400">Notas internas</div>
                        
                        {notes.trim().length > 0 && (
                            <button 
                                onClick={() => {
                                    onShowToast('Nota guardada correctamente', 'success');
                                    setNotes('');
                                }}
                                className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded transition-colors hover:bg-emerald-100"
                            >
                                ✓ Guardar nota
                            </button>
                        )}
                    </div>
                    
                    <textarea 
                        className="w-full bg-white border border-gray-300 rounded-lg p-[10px] px-[12px] text-gray-900 text-[12px] resize-none outline-none focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20 placeholder:text-gray-400 transition-colors" 
                        rows={3} 
                        placeholder="Agrega notas visibles solo para el equipo de soporte..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    ></textarea>
                </div>

                <div className="p-[20px] flex flex-col gap-[8px] mt-auto">
                    {selectedReq.status !== 'approved' && selectedReq.status !== 'rejected' && (
                        <>
                            <button disabled={isUpdating || loadingDetail} className="w-full py-[9px] rounded-lg text-[12px] font-semibold transition-all bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm disabled:opacity-50" onClick={onApprove}>
                                ✓ Aprobar identidad
                            </button>
                            <button disabled={isUpdating || loadingDetail} className="w-full py-[9px] rounded-lg text-[12px] font-semibold transition-all bg-white border border-amber-300 text-amber-600 hover:bg-amber-50 shadow-sm disabled:opacity-50" onClick={() => { onShowToast('Notificación enviada al usuario', 'info'); onClose(); }}>
                                Solicitar docs faltantes
                            </button>
                            <button disabled={isUpdating || loadingDetail} className="w-full py-[9px] rounded-lg text-[12px] font-semibold transition-all bg-white border border-red-300 text-red-600 hover:bg-red-50 shadow-sm disabled:opacity-50" onClick={onReject}>
                                ✗ Rechazar solicitud
                            </button>
                        </>
                    )}
                    <button disabled={isUpdating} className="w-full py-[9px] rounded-lg text-[12px] font-semibold transition-all bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-50" onClick={onClose}>
                        Cerrar panel
                    </button>
                </div>

            </div>
        </>
    );
};

export default ValidacionDrawer;